import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AppIcon } from './LucideIcon';
import './overflow-scroller.css';

/**
 * A horizontal scroll container that shows a chevron affordance at whichever
 * edge still has content out of view, so an overflowing row (e.g. the workspace
 * step tabs) reads as scrollable instead of silently clipped. The arrows appear
 * only when they can do something and disappear at each end; clicking one nudges
 * the row. Purely an affordance — `tabIndex={-1}` keeps them out of the tab order
 * since the scrolled controls are already reachable.
 */
export default function OverflowScroller({
  className,
  children,
  role,
  ariaLabel,
}: {
  /** Applied to the scrolling element itself, so callers keep their own layout. */
  className?: string;
  children: ReactNode;
  role?: string;
  ariaLabel?: string;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    /* 1px slack: sub-pixel widths otherwise leave an arrow stuck on at an end. */
    const left = scrollLeft > 1;
    const right = scrollLeft + clientWidth < scrollWidth - 1;
    // Keep the previous object when neither edge moved. Measuring runs after
    // every render, so storing a fresh object unconditionally would schedule
    // another render and loop without end.
    setEdges((previous) =>
      previous.left === left && previous.right === right ? previous : { left, right },
    );
  }, []);

  // Re-measure after every render: the scrolled content can change without the
  // container resizing (a step tab added or removed), and neither the scroll
  // event nor the ResizeObserver reports that.
  useEffect(measure);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', measure, { passive: true });
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      el.removeEventListener('scroll', measure);
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  const nudge = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(120, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  return (
    <div className="pnid-oflow">
      {edges.left && (
        <button
          type="button"
          className="pnid-oflow__arrow pnid-oflow__arrow--left"
          aria-label="Scroll left"
          tabIndex={-1}
          onClick={() => nudge(-1)}
        >
          <AppIcon icon={ChevronLeft} size={16} />
        </button>
      )}
      <div ref={scrollerRef} className={className} role={role} aria-label={ariaLabel}>
        {children}
      </div>
      {edges.right && (
        <button
          type="button"
          className="pnid-oflow__arrow pnid-oflow__arrow--right"
          aria-label="Scroll right"
          tabIndex={-1}
          onClick={() => nudge(1)}
        >
          <AppIcon icon={ChevronRight} size={16} />
        </button>
      )}
    </div>
  );
}
